import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Zap, LogOut, LayoutDashboard, FileText, DollarSign, Package, Users, Megaphone, Cpu, BarChart3, MessageSquare, Activity, Target, Handshake, SunMedium, ShoppingBag, PlugZap, Boxes, FileSpreadsheet, Search, Send, Sparkles, Brain, ShieldCheck, Inbox, Briefcase } from 'lucide-react';
import useAdminData from '@/hooks/useAdminData';
import OverviewTab from '@/components/admin/OverviewTab';
import ControlCenterTab from '@/components/admin/ControlCenterTab';
import AdminSidebar from '@/components/admin/AdminSidebar';
import ApprovalInboxTab from '@/components/admin/ApprovalInboxTab';
import ReportsTab from '@/components/admin/ReportsTab';
import TransactionsTab from '@/components/admin/TransactionsTab';
import AgentTasksTab from '@/components/admin/AgentTasksTab';
import AffiliateProductsTab from '@/components/admin/AffiliateProductsTab';
import InstallersTab from '@/components/admin/InstallersTab';
import MarketingTab from '@/components/admin/MarketingTab';
import ClickAnalyticsTab from '@/components/admin/ClickAnalyticsTab';
import LeadsTab from '@/components/admin/LeadsTab';
import EnergyControlLeadsTab from '@/components/admin/EnergyControlLeadsTab';
import EnergyControlProductsTab from '@/components/admin/EnergyControlProductsTab';
import FunnelTab from '@/components/admin/FunnelTab';
import AdsTab from '@/components/admin/AdsTab';
import DriversTab from '@/components/admin/DriversTab';
import ComponentsTab from '@/components/admin/ComponentsTab';
import QuoteRequestsTab from '@/components/admin/QuoteRequestsTab';
import InstallerRevenueTab from '@/components/admin/InstallerRevenueTab';
import ProviderManagerPanel from '@/components/admin/ProviderManagerPanel';
import ProviderEnrichmentTab from '@/components/admin/ProviderEnrichmentTab';
import OutreachPoolTab from '@/components/admin/OutreachPoolTab';
import AdsControlAgentTab from '@/components/admin/AdsControlAgentTab';
import AdsAutomationAgentTab from '@/components/admin/AdsAutomationAgentTab';
import AdsV2Tab from '@/components/admin/AdsV2Tab';
import DeviceDiscoveryTab from '@/components/admin/DeviceDiscoveryTab';
import HomeySimulationTab from '@/components/admin/HomeySimulationTab';
import HomeyPhase3SafetyTab from '@/components/admin/HomeyPhase3SafetyTab';
import DeviceOnboardingTab from '@/components/admin/DeviceOnboardingTab';
import AffiliateAgentTab from '@/components/admin/AffiliateAgentTab';
import DeviceHubAdminTab from '@/components/admin/DeviceHubAdminTab';
import DeviceIntelligenceTab from '@/components/admin/DeviceIntelligenceTab';
import PilotDevicesTab from '@/components/admin/PilotDevicesTab';
import AdminQuotesTab from '@/components/admin/AdminQuotesTab';
import AdminCustomersTab from '@/components/admin/AdminCustomersTab';
import AdminEmailQuotaBanner from '@/components/admin/AdminEmailQuotaBanner';
import { AdminGroupNavDesktop, AdminGroupNavMobile, MENU_GROUPS } from '@/components/admin/AdminMenuGroups';

const TABS = [
  { id: 'control_center', label: 'Control Center', icon: ShieldCheck },
  { id: 'approval_inbox', label: 'Approval Inbox', icon: Inbox },
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'funnel', label: 'Funnel', icon: Activity },
  { id: 'ads', label: 'Ads', icon: Target },
  { id: 'ads_v2', label: 'Ads V2 Rebuild', icon: Target },
  { id: 'ads_control_agent', label: 'Ads Control Agent', icon: Sparkles },
  { id: 'ads_automation_agent', label: 'Ads Automation Agent', icon: Brain },
  { id: 'affiliate_agent', label: 'Affiliate Agent', icon: Handshake },
  { id: 'device_hub', label: 'Device Hub', icon: PlugZap },
  { id: 'device_discovery', label: 'Device Discovery 5A/5B', icon: Cpu },
  { id: 'homey_simulation', label: 'Homey Simulation (Phase 2)', icon: Cpu },
  { id: 'homey_phase3', label: 'Homey Phase 3 Safety', icon: Cpu },
  { id: 'device_onboarding', label: 'Device Onboarding', icon: Cpu },
  { id: 'pilot_devices', label: 'Pilot Access (Mijn Powersure)', icon: Users },
  { id: 'device_intelligence', label: 'Device Intelligence', icon: Brain, requiresFlag: 'dia_admin_enabled' },
  { id: 'reports', label: 'Reports', icon: FileText },
  { id: 'transactions', label: 'Transactions', icon: DollarSign },
  { id: 'products', label: 'Products', icon: Package },
  { id: 'installers', label: 'Installers', icon: Users },
  { id: 'leads', label: 'Leads', icon: MessageSquare },
  { id: 'customers', label: 'Klanten', icon: Users },
  { id: 'ec_leads', label: 'Energy Control Leads', icon: SunMedium },
  { id: 'ec_products', label: 'Productbeheer', icon: ShoppingBag },
  { id: 'drivers', label: 'Drivers', icon: PlugZap },
  { id: 'components', label: 'Components', icon: Boxes },
  { id: 'quote_requests', label: 'Offerteaanvragen', icon: FileSpreadsheet },
  { id: 'quotes', label: 'Offertes', icon: FileText },
  { id: 'installer_revenue', label: 'Installer Revenue', icon: Briefcase },
  { id: 'providers', label: 'Providers', icon: Handshake },
  { id: 'provider_enrichment', label: 'Provider Enrichment', icon: Search },
  { id: 'outreach_pool', label: 'Outreach Pool', icon: Send },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'marketing', label: 'Marketing', icon: Megaphone },
  { id: 'tasks', label: 'Agent Tasks', icon: Cpu },
];

export default function AdminPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = (() => {
    const t = searchParams.get('tab');
    if (t && TABS.some((x) => x.id === t)) return t;
    return 'control_center';
  })();
  const ownerAppMode = searchParams.get('app') === 'owner';
  const [token, setToken] = useState(() => localStorage.getItem('admin_token'));
  const [activeTab, setActiveTab] = useState(initialTab);
  const [publicFlags, setPublicFlags] = useState({});

  useEffect(() => {
    if (!token) {
      // Preserve owner-mode flag through the login round-trip so the PWA
      // start_url (/admin?app=owner) keeps its owner UX after re-login.
      const qs = ownerAppMode ? '?app=owner' : '';
      navigate(`/admin/login${qs}`);
    }
  }, [token, ownerAppMode, navigate]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/features/public`)
      .then(r => r.ok ? r.json() : {})
      .then(d => setPublicFlags(d || {}))
      .catch(() => setPublicFlags({}));
  }, []);

  const visibleTabs = TABS.filter(t => !t.requiresFlag || publicFlags[t.requiresFlag]);

  const admin = useAdminData(token);

  const handleLogout = async () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  if (!token) {
    return null; // useEffect handles redirect to login
  }

  if (admin.loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-3">
        <Zap className="w-10 h-10 text-primary animate-pulse" />
        <p className="text-sm text-muted-foreground">Loading dashboard...</p>
      </div>
    );
  }

  if (!admin.stats) {
    // Stats failed after retries — could be auth issue or extended cold start
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 px-6">
        <Zap className="w-10 h-10 text-primary" />
        <p className="text-sm text-muted-foreground text-center">Dashboard is loading. If this persists, try refreshing.</p>
        <div className="flex gap-3">
          <button onClick={() => window.location.reload()} className="px-4 py-2 text-sm bg-primary text-white rounded-lg">
            Refresh
          </button>
          <button onClick={() => { localStorage.removeItem('admin_token'); navigate('/admin/login'); }} className="px-4 py-2 text-sm bg-muted text-foreground rounded-lg">
            Re-login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen"
      data-testid="admin-page"
      data-app-mode={ownerAppMode ? 'owner' : 'standard'}
      style={
        activeTab === 'control_center'
          ? { background: 'linear-gradient(180deg, #0a1410 0%, #050a08 100%)' }
          : undefined
      }
    >
      {/* Nav */}
      <nav className={`sticky top-0 z-50 backdrop-blur-md border-b py-3 ${
        activeTab === 'control_center'
          ? 'bg-[#0e1b16]/85 border-white/10'
          : 'bg-white/80 border-border'
      }`}>
        <div className="container mx-auto px-4 sm:px-6 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate(ownerAppMode ? '/admin?app=owner' : '/')}>
            <Zap className={`w-5 h-5 ${activeTab === 'control_center' ? 'text-emerald-300' : 'text-primary'}`} />
            <span className={`text-base sm:text-lg font-light ${activeTab === 'control_center' ? 'text-white' : 'text-primary'}`} style={{ fontFamily: 'Outfit' }}>
              Powersure {ownerAppMode ? 'Control' : 'Admin'}
            </span>
            {ownerAppMode && (
              <span className={`hidden sm:inline-block text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded font-medium ${
                activeTab === 'control_center'
                  ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-400/30'
                  : 'bg-primary/10 text-primary'
              }`} data-testid="admin-owner-mode-badge">
                Owner
              </span>
            )}
          </div>
          <button onClick={handleLogout} className={`text-xs sm:text-sm flex items-center gap-1 transition-colors ${
            activeTab === 'control_center'
              ? 'text-white/65 hover:text-emerald-300'
              : 'text-muted-foreground hover:text-primary'
          }`} data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 py-4 sm:py-6">
        <div className="max-w-7xl mx-auto">
          <AdminEmailQuotaBanner />
          {/* Owner cockpit nav — 8 hoofdgroepen, geen platte 32-tab lijst */}
          {(() => {
            const badges = {};
            if (admin.stats.pending_products > 0) badges.products = admin.stats.pending_products;
            if (admin.stats.pending_installers > 0) badges.installers = admin.stats.pending_installers;
            if (admin.stats.running_tasks > 0) badges.tasks = admin.stats.running_tasks;
            const tabIds = visibleTabs.map((t) => t.id);
            // Filter tab-ids zodat alleen die binnen MENU_GROUPS zitten worden getoond.
            const knownIds = new Set(MENU_GROUPS.flatMap((g) => g.tabs.map((t) => t.id)));
            const visibleKnown = tabIds.filter((id) => knownIds.has(id));
            return (
              <>
                <AdminGroupNavMobile
                  activeTab={activeTab}
                  onChange={setActiveTab}
                  visibleTabIds={visibleKnown}
                  badges={badges}
                />
                <AdminGroupNavDesktop
                  activeTab={activeTab}
                  onChange={setActiveTab}
                  visibleTabIds={visibleKnown}
                  badges={badges}
                />
              </>
            );
          })()}

          {/* Tab content */}
          {activeTab === 'control_center' && (
            <div className="flex flex-col lg:flex-row gap-4 items-start">
              <AdminSidebar
                activeTab={activeTab}
                onChange={setActiveTab}
                availableTabIds={visibleTabs.map(t => t.id)}
              />
              <div className="flex-1 min-w-0 w-full">
                <ControlCenterTab token={token} onNavigateToInbox={() => setActiveTab('approval_inbox')} onNavigateToTab={(t) => setActiveTab(t)} />
              </div>
            </div>
          )}
          {activeTab === 'pilot_devices' && <PilotDevicesTab token={token} />}
          {activeTab === 'approval_inbox' && <ApprovalInboxTab token={token} />}
          {activeTab === 'overview' && <OverviewTab stats={admin.stats} reports={admin.reports} transactions={admin.transactions} token={token} />}
          {activeTab === 'reports' && <ReportsTab reports={admin.reports} />}
          {activeTab === 'transactions' && <TransactionsTab transactions={admin.transactions} />}
          {activeTab === 'products' && (
            <AffiliateProductsTab
              token={token}
              products={admin.products}
              onApprove={admin.approveProduct}
              onReject={admin.rejectProduct}
              onUpdateLink={admin.updateProductLink}
              onResearch={admin.triggerAffiliateResearch}
              onReviewCheck={admin.triggerReviewCheck}
              onBatchReview={admin.triggerBatchReviewCheck}
              onSuggestLink={admin.suggestLinkForProduct}
              onSuggestBatch={admin.suggestLinksBatch}
              onUpdateSuggestion={admin.updateSuggestion}
              onImported={admin.reloadProducts || (() => window.location.reload())}
            />
          )}
          {activeTab === 'installers' && (
            <InstallersTab
              installers={admin.installers}
              onApprove={admin.approveInstaller}
              onReject={admin.rejectInstaller}
              onResearch={admin.triggerInstallerResearch}
            />
          )}
          {activeTab === 'marketing' && (
            <MarketingTab
              campaigns={admin.campaigns}
              onApprove={admin.approveCampaign}
              onReject={admin.rejectCampaign}
              onGenerate={admin.triggerMarketingGenerate}
              onRefresh={admin.refreshCampaign}
              onUpdatePerformance={admin.updateCampaignPerformance}
              onGetAngles={admin.getAudienceAngles}
            />
          )}
          {activeTab === 'tasks' && <AgentTasksTab tasks={admin.tasks} onRefresh={admin.reloadTasks} />}
          {activeTab === 'analytics' && <ClickAnalyticsTab token={token} />}
          {activeTab === 'leads' && <LeadsTab token={token} />}
          {activeTab === 'customers' && <AdminCustomersTab token={token} />}
          {activeTab === 'ec_leads' && <EnergyControlLeadsTab token={token} />}
          {activeTab === 'ec_products' && <EnergyControlProductsTab token={token} />}
          {activeTab === 'drivers' && <DriversTab token={token} />}
          {activeTab === 'components' && <ComponentsTab token={token} />}
          {activeTab === 'quote_requests' && <QuoteRequestsTab token={token} />}
          {activeTab === 'quotes' && <AdminQuotesTab token={token} />}
          {activeTab === 'installer_revenue' && <InstallerRevenueTab token={token} />}
          {activeTab === 'providers' && <ProviderManagerPanel token={token} />}
          {activeTab === 'provider_enrichment' && <ProviderEnrichmentTab token={token} />}
          {activeTab === 'outreach_pool' && <OutreachPoolTab token={token} />}
          {activeTab === 'funnel' && <FunnelTab token={token} />}
          {activeTab === 'ads' && <AdsTab token={token} />}
          {activeTab === 'ads_v2' && <AdsV2Tab token={token} />}
          {activeTab === 'ads_control_agent' && <AdsControlAgentTab token={token} />}
          {activeTab === 'ads_automation_agent' && <AdsAutomationAgentTab token={token} />}
          {activeTab === 'affiliate_agent' && <AffiliateAgentTab token={token} />}
          {activeTab === 'device_hub' && <DeviceHubAdminTab token={token} />}
          {activeTab === 'device_discovery' && <DeviceDiscoveryTab token={token} />}
          {activeTab === 'homey_simulation' && <HomeySimulationTab token={token} />}
          {activeTab === 'homey_phase3' && <HomeyPhase3SafetyTab token={token} />}
          {activeTab === 'device_onboarding' && <DeviceOnboardingTab token={token} />}
          {activeTab === 'homey_simulation' && <HomeySimulationTab token={token} />}
          {activeTab === 'device_intelligence' && publicFlags.dia_admin_enabled && <DeviceIntelligenceTab token={token} />}
        </div>
      </div>
    </div>
  );
}
